'use client';

import ReactSelectComponents from '@/customizations/ReactSelect';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { DateInput as MDateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import Image from 'next/image';
import React, { ForwardedRef, useEffect, useState } from 'react';
import {
  FormState,
  RegisterOptions,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  useForm,
} from 'react-hook-form';
import ReactSelect from 'react-select';

export interface FormConfig {
  initialValues?: any;
  validation?: { [key: string]: RegisterOptions };
}

const FieldWrapper = ({ className = '', fieldState, children }: any) => (
  <div
    className={`field-wrapper relative ${fieldState?.isTouched ? 'touched' : ''} ${fieldState?.isDirty ? 'dirty' : ''} ${
      fieldState?.hasValue ? 'has-value' : ''
    } ${fieldState?.error ? 'has-error' : ''} ${className}`}
  >
    {children}
  </div>
);

const FieldData = ({ label, info, error, labelClassName = '' }: any) => (
  <>
    {label && <label className={`field-label ${labelClassName}`}>{label}</label>}
    {info && <div className="field-info">{info}</div>}
    {error && <span className="field-error">{error}</span>}
  </>
);

interface IFieldMapper {
  children: any;
  config: FormConfig;
  register: UseFormRegister<any>;
  trigger: UseFormTrigger<any>;
  getFieldState: UseFormGetFieldState<any>;
  getValues: UseFormGetValues<any>;
  formState: FormState<any>;
  setValue: UseFormSetValue<any>;
  level?: number;
}

const fieldMapper = ({ children, config, register, trigger, getFieldState, getValues, formState, setValue, level = 0 }: IFieldMapper) => {
  return Array.isArray(children)
    ? children.map((child, index) => {
        let props: any = { key: `_${level}_${index}_`, ...child?.props };
        if (child?.props?.children?.length > 0) {
          props.children = fieldMapper({
            children: child.props.children,
            config,
            register,
            trigger,
            getFieldState,
            getValues,
            formState,
            setValue,
            level: level + 1,
          });
        }

        if (child?.props?.name) {
          const validationRules = config?.validation?.[child.props.name];

          let rules = {};
          if (validationRules) {
            rules = typeof validationRules === 'function' ? { validate: validationRules } : validationRules;
          }
          props = {
            ...props,
            ...child.props,
            registrationProps: register(child.props.name, rules),
            trigger,
            setValue: (value: any) => setValue(child.props.name, value),
            key: child.props.name,
            value: getValues(child.props.name),
            fieldState: {
              ...getFieldState(child.props.name),
              hasValue: getValues(child.props.name),
              error: formState.errors[child.props.name]?.message,
            },
          };

          return React.createElement(child?.type, {
            ...props,
          });
        }

        if (child?.type) {
          return React.createElement(child?.type, {
            ...props,
          });
        }
        return child;
      })
    : children;
};

export const HookForm = React.forwardRef<
  any,
  {
    config: FormConfig;
    className?: string;
    children: any;
    onSubmit: (formData: any) => void;
  }
>(({ config, className, children, onSubmit }, ref) => {
  const { handleSubmit, register, formState, trigger, setValue, getValues, getFieldState, watch } = useForm({
    defaultValues: config?.initialValues || {},
    mode: 'all',
  });
  watch();
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="hook-form" ref={ref}>
        <div className={`text-xl ${className}`}>
          {fieldMapper({
            children,
            config,
            register,
            trigger,
            getFieldState,
            getValues,
            formState,
            setValue,
          })}
        </div>
      </form>
    </>
  );
});

export const Input = ({
  registrationProps,
  trigger,
  className,
  label,
  fieldState,
  info,
  setValue,
  addOnRight = null,
  specialValue,
  ...rest
}: any) => {
  useEffect(() => {
    if (specialValue) {
      setValue(specialValue);
    }
  }, [specialValue]);
  return (
    <FieldWrapper {...{ className, fieldState }}>
      <div className={`field-group flex h-full ${Boolean(label) ? 'labeled' : ''}`}>
        <input
          {...registrationProps}
          {...rest}
          className={`form-field`}
          onFocus={(e) => (e.target.parentNode as HTMLDivElement).classList.add('focus')}
          onBlur={(e) => (e.target.parentNode as HTMLDivElement).classList.remove('focus')}
          //value={value}
          // onChange={(e) => {
          //   console.log(e.target.value);
          //   setValue(e.target.value);
          //   onChange?.(e.target.value);
          // }}
        />
        {addOnRight}
      </div>
      <FieldData {...{ label, info, error: fieldState?.error }} />
    </FieldWrapper>
  );
};

export const Checkbox = ({ registrationProps, trigger, className, children, fieldState, setValue, name, defaultChecked, ...rest }: any) => {
  const [isChecked, setIsChecked] = useState(defaultChecked || false);
  return (
    <FieldWrapper {...{ className, fieldState }}>
      <label className="checkbox">
        <input
          type="checkbox"
          {...registrationProps}
          {...rest}
          value={isChecked}
          className={`form-field cursor-pointer`}
          onChange={(e) => {
            setIsChecked(e.target.checked);
          }}
        />
        <div>{children}</div>
      </label>
      {fieldState?.error && <span className="field-error">{fieldState?.error}</span>}
    </FieldWrapper>
  );
};

export const Radio = ({ registrationProps, trigger, className, children, fieldState, setValue, ...rest }: any) => {
  return (
    <FieldWrapper {...{ className, fieldState }}>
      <label className="radio">
        <input type="radio" {...registrationProps} {...rest} className={`form-field`} />
        <div>{children}</div>
      </label>
      {fieldState?.error && <span className="field-error">{fieldState?.error}</span>}
    </FieldWrapper>
  );
};

export const TextArea = ({
  registrationProps,
  trigger,
  className,
  label,
  labelClassName = '',
  fieldState,
  info,
  setValue,
  value,
  onChange,
  row = 5,
  ...rest
}: any) => {
  return (
    <FieldWrapper {...{ className, fieldState }}>
      <textarea {...registrationProps} rows={row} {...rest} className={`form-field`} style={{ resize: 'none' }} />
      <FieldData
        {...{
          label,
          info,
          error: fieldState?.error,
          labelClassName,
        }}
      />
    </FieldWrapper>
  );
};

export const Select = ({
  registrationProps,
  trigger,
  searchable = true,
  disabled = false,
  value,
  options,
  className,
  label,
  fieldState,
  info,
  setValue,
  onChange,
  defaultValue,
  ...rest
}: any) => {
  //console.log(rest, 'rest');
  const [option, setOption] = useState<any>(options?.find((opt: { label: string; value: string }) => opt.value === defaultValue));
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let foundOption = options?.find((opt: { label: string; value: string }) => opt.value === (value || defaultValue));
    setOption(foundOption || null);
    setValue?.(foundOption?.value || null);
  }, [options, value, defaultValue]);
  return (
    <FieldWrapper className={`${className} ${option ? 'has-value' : ''}`} {...{ fieldState }}>
      <ReactSelect
        className="react-select"
        name={registrationProps?.name}
        // onFocus={() => setFocus(true)}
        // onBlur={() => setFocus(false)}
        // onMenuOpen={() => setOpen(true)}
        onMenuClose={() => setOpen(false)}
        onChange={(selectedOption: any) => {
          onChange(selectedOption);
          setOption(selectedOption);
          setValue?.(selectedOption?.value);
          trigger?.(registrationProps?.name);
        }}
        defaultValue={defaultValue}
        isSearchable={searchable}
        isDisabled={disabled}
        placeholder=""
        unstyled={true}
        value={option}
        menuPlacement="bottom"
        components={ReactSelectComponents({ focus, open, hasLabel: Boolean(label) })}
        options={options}
        {...rest}
      />
      <FieldData {...{ label, info, error: fieldState?.error }} />
    </FieldWrapper>
  );
};

export const DateInput = ({ registrationProps, trigger, className, label, fieldState, info, dateFormat = 'DD-MMM-YYYY', setValue, ...rest }: any) => {
  const [date, setDate] = useState<Date | null>(dayjs(fieldState.hasValue === '' ? undefined : fieldState.hasValue, dateFormat).toDate());

  const onChange = (date: any) => {
    setDate(date);
    setValue(dayjs(date).format(dateFormat));
    trigger(registrationProps.name);
  };

  return (
    <>
      <FieldWrapper {...{ className, fieldState }}>
        <MDateInput
          maxDate={new Date()}
          valueFormat={dateFormat}
          value={date}
          onChange={onChange}
          classNames={{
            levelsGroup: 'custom-date-input',
            day: 'custom-day-input',
            calendarHeaderLevel: 'custom-year-input',
            weekday: 'custom-week-input',
          }}
          className="form-field pointer-events-none absolute"
          style={{ border: 'none' }}
          styles={(theme) => ({
            wrapper: {
              opacity: 0,
            },
            input: {
              pointerEvents: 'all',
            },
            calendar: {
              pointerEvents: 'all',
            },
          })}
        />
        <input {...registrationProps} {...rest} className={`form-field`} />
        <FieldData {...{ label, info, error: fieldState?.error }} />
      </FieldWrapper>
      <style jsx global>{`
        .custom-date-input,
        .custom-day-input,
        .custom-year-input,
        .custom-week-input {
          font-size: 15px !important;
        }

        .custom-date-input {
          font-family: 'Figtree', sans-serif;
        }
      `}</style>
    </>
  );
};

export const FileUpload = ({ className, selectedFiles, setSelectedFiles }: { className: string; selectedFiles: any; setSelectedFiles: Function }) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);

      const validFiles = newFiles.filter((file) => {
        if (file.size > 10 * 1024 * 1024) {
          openNotification('danger', 'File Upload', `${file.name} exceeds the 10MB size limit and will not be added.`);
          return false;
        }
        return true;
      });

      const totalFiles = selectedFiles.length + validFiles.length;

      if (totalFiles > 3) {
        openNotification('danger', 'File Upload', 'You can only upload up to 3 files.');
      } else {
        const filesWithPreview = validFiles.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
        }));

        setSelectedFiles((prevFiles: any) => [...prevFiles, ...filesWithPreview]);
      }
    }
  };
  const handleUpload = async () => {
    const formData = new FormData();
    selectedFiles.forEach((file: string | Blob) => {
      formData.append('files', file);
    });
  };
  return (
    <>
      <div className={`file-upload-container cursor-pointer ${className}`}>
        <input type="file" id="myFile" name="filename" className="file-input" multiple onChange={handleFileChange} accept="image/*" />
        <label
          htmlFor="myFile"
          onClick={handleUpload}
          className={`flex w-full flex-grow items-center justify-center rounded border border-black p-4 text-xl`}
        >
          <div className="flex gap-x-2">
            <Image src="/images/attach-file.svg" alt={'attach-file'} height={18} width={18} />
            <p className="font-medium text-black">Attach Images </p>
          </div>
        </label>
      </div>
      <p className="text-gray-500">maximum: 3 images</p>
    </>
  );
};
